import {
  convertEventSelectToTarget,
  DataTable,
  FormFeedBack,
  IOptions,
  ModalConfirmAction,
  PersonBadge,
  SelectRemote,
  STATUSPAGE,
} from '@repo/ui';
import {
  AddPersonToGroupUseCaseInterface,
  GetAllPersonsFromGroupUseCaseInterface,
  GetAllStudentsNotPaginatedUseCaseInterface,
  ImportPersonsFromAnotherGroupUseCaseInterface,
  RemovePersonFromGroupUseCaseInterface,
} from 'domain/usecases';
import { PROFILE_TYPE } from 'domain/value-objects/profile-type';
import { useFormik } from 'formik';
import { notifyError, notifySuccess } from 'presentation/app/components/notify';
import { useLegends } from 'presentation/app/hooks/useLegends';
import { useService } from 'presentation/app/hooks/useService';
import { useAppSelector } from 'presentation/config/hooks/useRedux';
import {
  clearSelectedGroupStudentsListData,
  loadSelectedGroupStudentsListData,
} from 'presentation/config/store/groups/group-settings-slice';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button, CardBody, Col, Form, Row, Spinner } from 'reactstrap';
import * as Yup from 'yup';

import noDataBg from '../../../../../assets/images/no-task.png';
import { makeColumnsGroupSettingsPersonsFromGroupList } from '../../../common/columns/group-settings/columns-group-settings-persons-from-group-list';
import { ImportEntitiesFromAnotherGroupModal } from '../import-entities-from-another-group-modal';

interface ICustomStudentSelectOption extends IOptions {
  tags: {
    key: string;
    color: string;
    label: string;
  }[];
}

interface IProps {
  groupId: string;
}

const statusInitialStates = {
  confirmRemovalFromGroupModalBtnsStatus: STATUSPAGE.DONE,
  importStudentsFromAnotherGroupModalBtnsStatus: STATUSPAGE.DONE,
  studentRemoteSelectInputStatus: STATUSPAGE.DONE,
  addStudentToGroupBtnStatus: STATUSPAGE.DONE,
  studentsFromGroupListStatus: STATUSPAGE.DONE,
};

export const StudentsTabContent = ({ groupId }: IProps) => {
  // Hooks
  const { studentService, groupService } = useService();
  const dispatch = useDispatch();
  const { getLegends } = useLegends();

  // States
  const [
    isConfirmRemovalFromGroupModalOpen,
    setIsConfirmRemovalFromGroupModalOpen,
  ] = useState(false);
  const [
    isImportStudentsFromAnotherGroupModalOpen,
    setIsImportStudentsFromAnotherGroupModalOpen,
  ] = useState(false);
  const [
    selectedStudentFromGroupRelationId,
    setSelectedStudentFromGroupRelationId,
  ] = useState<string | null>(null);
  const [statusStates, setStatusStates] = useState(statusInitialStates);
  const { selectedGroupStudentsListData } = useAppSelector(
    (state) => state.groupSettings,
  );
  const [studentSelectOptions, setStudentSelectOptions] =
    useState<GetAllStudentsNotPaginatedUseCaseInterface.Result>(null);
  const studentOptions: ICustomStudentSelectOption[] =
    studentSelectOptions?.data?.map((option) => {
      const { id, cpf, name, tags } = option;
      return {
        value: id,
        label: `${cpf} - ${name}`,
        tags,
      };
    });

  // Methods
  const changeStatusState = useCallback(
    <T extends keyof typeof statusInitialStates>(key: T, value: STATUSPAGE) => {
      setStatusStates((prevState) => ({
        ...prevState,
        [key]: value,
      }));
    },
    [],
  );

  const toggleConfirmRemovalFromGroupModal = useCallback(
    () => setIsConfirmRemovalFromGroupModalOpen((prevState) => !prevState),
    [],
  );

  const toggleImportStudentsFromAnotherGroupModal = useCallback(
    () =>
      setIsImportStudentsFromAnotherGroupModalOpen((prevState) => !prevState),
    [],
  );

  const requestGetAllStudentsFromGroup = useCallback(
    async (params: GetAllPersonsFromGroupUseCaseInterface.Params) => {
      changeStatusState('studentsFromGroupListStatus', STATUSPAGE.LOADING);
      await groupService
        .getAllPersonsFromGroup(params)
        .then((res) => dispatch(loadSelectedGroupStudentsListData(res)))
        .catch((err) => {
          notifyError(err.message);
          dispatch(clearSelectedGroupStudentsListData());
        })
        .finally(() =>
          changeStatusState('studentsFromGroupListStatus', STATUSPAGE.DONE),
        );
    },
    [],
  );

  const requestGetAllStudentsForSelectOptions = useCallback(
    async (params: GetAllStudentsNotPaginatedUseCaseInterface.Params) => {
      changeStatusState('studentRemoteSelectInputStatus', STATUSPAGE.LOADING);
      await studentService
        .getAllStudentsNotPaginated(params)
        .then((res) => setStudentSelectOptions(res))
        .catch((err) => {
          notifyError(err.message);
          setStudentSelectOptions(null);
        })
        .finally(() =>
          changeStatusState('studentRemoteSelectInputStatus', STATUSPAGE.DONE),
        );
    },
    [],
  );

  const requestAddStudentToGroup = useCallback(
    async (params: AddPersonToGroupUseCaseInterface.Params) => {
      changeStatusState('addStudentToGroupBtnStatus', STATUSPAGE.LOADING);
      await groupService
        .addPersonToGroup(params)
        .then((res) => {
          notifySuccess(res.message);
          requestGetAllStudentsFromGroup({
            groupId,
            personType: PROFILE_TYPE.STUDENT,
          });
        })
        .catch((err) => notifyError(err.message))
        .finally(() =>
          changeStatusState('addStudentToGroupBtnStatus', STATUSPAGE.DONE),
        );
    },
    [],
  );

  const requestRemoveStudentFromGroup = useCallback(
    async (params: RemovePersonFromGroupUseCaseInterface.Params) => {
      changeStatusState(
        'confirmRemovalFromGroupModalBtnsStatus',
        STATUSPAGE.LOADING,
      );
      await groupService
        .removePersonFromGroup(params)
        .then((res) => notifySuccess(res.message))
        .catch((err) => notifyError(err.message))
        .finally(() =>
          changeStatusState(
            'confirmRemovalFromGroupModalBtnsStatus',
            STATUSPAGE.DONE,
          ),
        );
    },
    [],
  );

  const requestImportStudentsFromAnotherGroup = useCallback(
    async (params: ImportPersonsFromAnotherGroupUseCaseInterface.Params) => {
      changeStatusState(
        'importStudentsFromAnotherGroupModalBtnsStatus',
        STATUSPAGE.LOADING,
      );
      await groupService
        .importPersonsFromAnotherGroup(params)
        .then((res) => {
          notifySuccess(res.message);
          requestGetAllStudentsFromGroup({
            groupId,
            personType: PROFILE_TYPE.STUDENT,
          });
        })
        .catch((err) => notifyError(err.message))
        .finally(() =>
          changeStatusState(
            'importStudentsFromAnotherGroupModalBtnsStatus',
            STATUSPAGE.DONE,
          ),
        );
    },
    [],
  );

  // Form Validation
  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      student: null,
    },

    validationSchema: Yup.object({
      student: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .required('Campo é obrigatório'),
    }),

    onSubmit: (values) => {
      const valuesToSubmit = {
        id: values.student?.value,
        group: groupId,
        personType: PROFILE_TYPE.STUDENT,
      };

      requestAddStudentToGroup(valuesToSubmit);
    },
  });

  useEffect(() => {
    requestGetAllStudentsFromGroup({
      groupId,
      personType: PROFILE_TYPE.STUDENT,
    });
  }, [groupId]);

  return (
    <>
      <Form
        autoComplete="off"
        onSubmit={(e) => {
          e.preventDefault();
          validation.handleSubmit();
          return false;
        }}
      >
        <CardBody>
          <Row>
            <Col lg={7} className="mb-3">
              <SelectRemote<ICustomStudentSelectOption>
                isClearable
                name="student"
                clearOptions={() => setStudentSelectOptions(null)}
                functionSearch={(searchString) => {
                  if (searchString.length === 0) {
                    setStudentSelectOptions(null);
                    return;
                  }

                  if (searchString.length < 3) return;

                  requestGetAllStudentsForSelectOptions({
                    query: searchString,
                  });
                }}
                inputId={'student'}
                onChange={(e) => {
                  validation.handleChange(
                    convertEventSelectToTarget('student', e),
                  );
                }}
                onBlur={(e) => {
                  setStudentSelectOptions(null);
                }}
                options={studentOptions}
                placeholder={`Selecione um ${getLegends('ESTUDANTE')}`}
                value={validation.values.student}
                loading={
                  statusStates.studentRemoteSelectInputStatus ===
                  STATUSPAGE.LOADING
                }
                isInvalid={
                  !!(validation.touched?.student && validation.errors?.student)
                }
                isDisabled={
                  statusStates.addStudentToGroupBtnStatus === STATUSPAGE.LOADING
                }
                formatOptionLabel={(data) => (
                  <div className="d-flex gap-2">
                    <div>{data.label}</div>
                    {data.tags?.map((tag, idx) => {
                      return <PersonBadge key={idx} tag={tag} />;
                    })}
                  </div>
                )}
              />
              <FormFeedBack
                error={String(validation.errors?.student)}
                isInvalid={
                  !!(validation.touched?.student && validation.errors?.student)
                }
              />
            </Col>
            <Col lg={5} className="mb-3">
              <div className="d-flex justify-content-end gap-4">
                <Button
                  type="submit"
                  disabled={
                    statusStates.addStudentToGroupBtnStatus ===
                    STATUSPAGE.LOADING
                  }
                  color="primary"
                >
                  {statusStates.addStudentToGroupBtnStatus ===
                    STATUSPAGE.LOADING && (
                    <Spinner size="sm">{['Carregando...']}</Spinner>
                  )}
                  Adicionar
                </Button>
                <Button
                  type="button"
                  onClick={() => toggleImportStudentsFromAnotherGroupModal()}
                  disabled={
                    statusStates.addStudentToGroupBtnStatus ===
                    STATUSPAGE.LOADING
                  }
                  color="primary"
                >
                  Importar de outro {getLegends('GRUPO')}
                </Button>
              </div>
            </Col>
          </Row>
          <Row>
            <DataTable
              noDataBg={noDataBg}
              actions={[
                {
                  id: 1,
                  name: 'remove',
                  label: 'Remover',
                  icon: 'ri-indeterminate-circle-fill',
                  classNameColorIcon: 'danger',
                  onPress: (cellProps) => {
                    setSelectedStudentFromGroupRelationId(cellProps.relationId);
                    toggleConfirmRemovalFromGroupModal();
                  },
                },
              ]}
              canSearch
              columns={makeColumnsGroupSettingsPersonsFromGroupList()}
              data={selectedGroupStudentsListData}
              loading={
                statusStates.studentsFromGroupListStatus === STATUSPAGE.LOADING
              }
            />
          </Row>
        </CardBody>
      </Form>

      <ModalConfirmAction
        modalTheme="danger"
        title={`Deseja remover o ${getLegends('ESTUDANTE')} do grupo?`}
        actionBtnLabel="Remover"
        isOpen={isConfirmRemovalFromGroupModalOpen}
        toggle={toggleConfirmRemovalFromGroupModal}
        onConfirm={() =>
          requestRemoveStudentFromGroup({
            relationId: selectedStudentFromGroupRelationId,
          })
        }
        loading={
          statusStates.confirmRemovalFromGroupModalBtnsStatus ===
          STATUSPAGE.LOADING
        }
      />

      <ImportEntitiesFromAnotherGroupModal
        isOpen={isImportStudentsFromAnotherGroupModalOpen}
        toggle={toggleImportStudentsFromAnotherGroupModal}
        importedEntitiesTitle={getLegends('ESTUDANTE', true)}
        buttonsLoading={
          statusStates.importStudentsFromAnotherGroupModalBtnsStatus ===
          STATUSPAGE.LOADING
        }
        requestImportEntitiesFromAnotherGroup={
          requestImportStudentsFromAnotherGroup
        }
        importedPersonType={PROFILE_TYPE.STUDENT}
        targetGroupId={groupId}
      />
    </>
  );
};
